import { User, ImagePlus, Wand2, BookOpen, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
// Optimized WebP images for better performance
import createAccountImage from "@/assets/how-it-works/create-account.webp";
import chooseStyleImage from "@/assets/how-it-works/choose-style.webp";
import readyToReadImage from "@/assets/how-it-works/ready-to-read.webp";
import PhotoToCharacterSlideshow from "./PhotoToCharacterSlideshow";

interface Step {
  number: string;
  icon: React.ElementType;
  title: string;
  description: string;
  media?: {
    type: "video" | "image" | "slideshow";
    src?: string;
    alt?: string;
  };
}

const steps: Step[] = [
  {
    number: "01",
    icon: User,
    title: "Skapa ett konto",
    description: "Registrera dig gratis på under en minut.\nInget betalkort krävs.",
    media: {
      type: "image",
      src: createAccountImage,
      alt: "Registreringsformulär för SnippSnapp",
    },
  },
  {
    number: "02",
    icon: ImagePlus,
    title: "Skapa en barnprofil",
    description: "Vi analyserar bilden och skapar en unik sagokaraktär som liknar ditt barn.",
    media: {
      type: "slideshow" as const,
      alt: "Foto förvandlas till AI-karaktär",
    },
  },
  {
    number: "03",
    icon: Wand2,
    title: "Välj tema, längd & stil",
    description: "Välj ett spännande äventyr, hur lång sagan ska vara och vilken illustrationsstil du gillar bäst.",
    media: {
      type: "image",
      src: chooseStyleImage,
      alt: "Välj illustrationsstil för din saga",
    },
  },
  {
    number: "04",
    icon: BookOpen,
    title: "Klar att läsa!",
    description: "Din personliga sagobok är redo på bara några minuter. Läs direkt på skärmen, ladda ner som PDF eller dela med nära och kära.",
    media: {
      type: "image",
      src: readyToReadImage,
      alt: "Saga öppen i SnippSnapp-appen på mobilen",
    },
  },
];

const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 overflow-hidden">
      <div className="container mx-auto">
        {/* Section header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-12 sm:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Så fungerar det
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Från foto till saga{" "}
            <span className="relative inline-block">
              {/* Sparkle effects */}
              <motion.span
                className="absolute -top-2 -left-1 text-sm"
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatDelay: 2,
                  ease: "easeInOut" 
                }}
              >
                ✨
              </motion.span>
              <motion.span
                className="absolute -top-1 -right-2 text-xs"
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatDelay: 2,
                  delay: 0.5,
                  ease: "easeInOut" 
                }}
              >
                ⭐
              </motion.span>
              <motion.span
                className="absolute -bottom-1 -right-1 text-xs"
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatDelay: 2,
                  delay: 1,
                  ease: "easeInOut" 
                }}
              >
                ✨
              </motion.span>
              
              {/* Book emoji with wobble */}
              <motion.span
                className="inline-block"
                animate={{ rotate: [0, -8, 8, -5, 5, 0] }}
                transition={{ 
                  duration: 2.5, 
                  repeat: Infinity, 
                  repeatDelay: 3,
                  ease: "easeInOut" 
                }}
              >
                📖
              </motion.span>
            </span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Skapa en personlig sagobok med ditt barn som huvudperson.
          </p>
        </motion.div>

        {/* Roadmap */}
        <div className="max-w-5xl mx-auto relative">
          {/* Steps container with relative positioning for the line */}
          <div className="relative">
            {/* Roadmap connection line (desktop) - now scoped to steps only */}
            <div className="absolute left-1/2 -translate-x-1/2 top-8 bottom-8 w-1 bg-gradient-to-b from-primary/40 via-teal/30 to-primary/20 rounded-full hidden lg:block" />

            {/* Steps */}
            <div className="space-y-8 md:space-y-6 lg:space-y-0">
            {steps.map((step, index) => {
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Tablet & Desktop layout - 2 columns with alternating sides */}
                  <div className={`md:grid md:grid-cols-2 md:gap-8 lg:gap-12 md:items-center`}>
                    {/* Content card - alternates between left/right */}
                    <div className={`${
                      isEven 
                        ? "md:col-start-1 md:text-right md:pr-4 lg:pr-12" 
                        : "md:col-start-2 md:text-left md:pl-4 lg:pl-12"
                    }`}>
                      <div className={`p-6 sm:p-8 rounded-3xl bg-card shadow-paper border border-border/50 ${
                        isEven ? "md:ml-auto" : "md:mr-auto"
                      } md:max-w-sm lg:max-w-md`}>
                        {/* Step number & icon */}
                        <div className={`flex items-center gap-3 mb-4 ${isEven ? "md:justify-end" : "md:justify-start"}`}>
                          <div className={`flex items-center gap-3 ${isEven ? "md:flex-row-reverse" : ""}`}>
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal to-primary shadow-magic flex items-center justify-center">
                              <step.icon className="w-6 h-6 text-white" />
                            </div>
                            <span className="font-display text-3xl font-bold text-primary/30">
                              {step.number}
                            </span>
                          </div>
                        </div>

                        <h3 className={`font-display text-xl sm:text-2xl font-bold text-foreground mb-2 ${isEven ? "md:text-right" : "md:text-left"}`}>
                          {step.title}
                        </h3>
                        <p className={`font-body text-muted-foreground leading-relaxed whitespace-pre-line ${isEven ? "md:text-right" : "md:text-left"}`}>
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {/* Media - alternates opposite to content */}
                    <div className={`mt-6 md:mt-0 ${
                      isEven 
                        ? "md:col-start-2 md:row-start-1" 
                        : "md:col-start-1 md:row-start-1"
                    }`}>
                      <div className={`${isEven ? "md:pl-4 lg:pl-12" : "md:pr-4 lg:pr-12"}`}>
                        <div className={`relative rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-primary/5 to-teal/5 border border-border/30 max-w-xs mx-auto md:max-w-none ${
                          step.number === "01" || step.number === "03" ? "aspect-[3/4]" : "aspect-square"
                        }`}>
                          {step.media ? (
                            step.media.type === "slideshow" ? (
                              <PhotoToCharacterSlideshow />
                            ) : step.media.type === "video" ? (
                              <video
                                src={step.media.src}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                src={step.media.src}
                                alt={step.media.alt || step.title}
                                className="w-full h-full object-cover"
                                width={step.number === "01" || step.number === "03" ? 480 : 480}
                                height={step.number === "01" || step.number === "03" ? 640 : 480}
                                loading={step.number === "01" ? "eager" : "lazy"}
                                decoding="async"
                              />
                            )
                          ) : (
                            // Placeholder for steps without media
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-center p-6">
                                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                  <step.icon className="w-10 h-10 text-primary/50" />
                                </div>
                                <p className="text-sm text-muted-foreground/60">
                                  Bild kommer snart
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Center node on the roadmap line (desktop) */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 hidden lg:flex">
                    <div className="w-5 h-5 rounded-full bg-primary shadow-magic border-4 border-background" />
                  </div>
                </motion.div>
              );
            })}
            </div>
          </div>

          {/* CTA Button */}
          <motion.div 
            className="text-center mt-16 sm:mt-20 lg:mt-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button asChild size="lg" variant="outline" className="group">
              <Link to="/how-it-works">
                Läs mer om hur du skapar din saga
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
